import { StepKey } from './steps';

export interface TourStep {
  // CSS selector for the element to highlight. Prefer `[data-tour="x"]`
  // attributes; fall back to stable class names if needed.
  selector: string;
  title: string;
  body: string;
}

// Tours keyed by section type. These fire when the user lands on a section
// editor of that type for the first time in a session — separate from the
// main 14-step onboarding walkthrough above. The goal is to teach "what can
// I do on this specific type of section" without forcing the user back
// through onboarding.
export const sectionTypeTours: Partial<Record<SectionType, TourStep[]>> = {
  Branch: [
    {
      body: 'Branch sections give guests a choice. Each path you add becomes a button they can pick — and each path can route to its own next section.',
      selector: '[data-tour="branch-paths"]',
      title: 'Offer a choice',
    },
    {
      body: 'Use Branch when you need a "choose your own adventure" — e.g. "Are you new here? / Returning member?" — and you want different content to follow each answer.',
      selector: '[data-tour="section-body-text"]',
      title: 'Set the question',
    },
  ],
  Checkin: [
    {
      body: 'Email is how guests log in and how OnArk recognizes them on return visits. The field is fixed — what you can edit is the welcome copy and any extra questions you want at sign-in (add a Form section right after to collect more).',
      selector: '[data-tour="checkin-email"]',
      title: 'Email is the key',
    },
    {
      body: 'Edit the welcome text — what guests see right above the email field. Set the tone here ("Welcome!", "Let\'s get started", etc.).',
      selector: '[data-tour="section-body-text"]',
      title: 'Welcome message',
    },
  ],
  Completion: [
    {
      body: 'The Completion section is the last page guests see. Tell them what to do next.',
      selector: '[data-tour="section-body-text"]',
      title: 'Wrap-up message',
    },
    {
      body: 'Add "Go to next class" cards — point a guest at another class once they finish this one. Great for funnels like Starting Point → Membership → Serve.',
      selector: '[data-tour="completion-goboxes"]',
      title: 'Recommend next steps',
    },
  ],
  Form: [
    {
      body: 'Click the section title to rename it. The name shows up in the section sidebar and on reports — pick something descriptive like "Contact info" or "Sign me up to serve".',
      selector: '[data-tour="section-title"]',
      title: 'Name your section',
    },
    {
      body: 'This is where the inputs live. Use the + button to add Short Text, Paragraph, Radio, Checkbox, Dropdown, Date, Time, Ministries picker, File upload, or Download.',
      selector: '[data-tour="form-inputs"]',
      title: 'Add questions',
    },
    {
      body: 'Add intro/help text that shows above the inputs. Useful for context — "This helps us follow up", "Pick all that apply", etc.',
      selector: '[data-tour="section-body-text"]',
      title: 'Add context (optional)',
    },
  ],
  Test: [
    {
      body: 'Tap “Pick a test” in the topbar to choose which assessment runs here — Spiritual Gifts, DISC, or Enneagram. OnArk handles the questions and scoring for you.',
      selector: '[data-tour="test-pick-button"]',
      title: 'Pick which test',
    },
    {
      body: 'Add intro text — what the test is and why guests should take it ("This 10-minute quiz helps us know where you might serve").',
      selector: '[data-tour="section-body-text"]',
      title: 'Explain the test',
    },
  ],
  Text: [
    {
      body: 'Text sections are just content — no input, no test, no video. Click the body to open the rich-text editor: headings, lists, links, embedded images.',
      selector: '[data-tour="section-body-text"]',
      title: 'Edit the content',
    },
    {
      body: 'The section title becomes the in-page heading. Make it count — "Why we exist", "Our beliefs", etc.',
      selector: '[data-tour="section-title"]',
      title: 'Headline',
    },
  ],
  Video: [
    {
      body: 'Paste a YouTube, Vimeo, or direct video link right here in the player. That’s the one thing this section needs to go live — later, “change video” in the card header swaps it.',
      selector: '[data-tour="video-source-setup"]',
      title: 'Add your video',
    },
    {
      body: 'Once the video is set, this player previews exactly what guests will see — sound, captions, autoplay timing, the works.',
      selector: '[data-tour="video-player"]',
      title: 'Live preview',
    },
    {
      body: 'Add a description above the video — what it’s about, how long it is, or what to listen for.',
      selector: '[data-tour="section-body-text"]',
      title: 'Description (optional)',
    },
  ],
};

// Per-step tours. Steps not listed here fall back to the bottom-sheet
// callout's text-only guidance. Keep tours focused — 3–6 hops max.
export const tours: Partial<Record<StepKey, TourStep[]>> = {
  automations: [
    {
      body: 'Automations attach to sections. Pick the section that should trigger the rule — usually a check-in or a sign-up Form.',
      selector: '[data-tour="automation-section-card"]',
      title: 'Step 1 — pick a trigger',
    },
    {
      body: 'Tap “Add Automation”. You can stack as many rules on the same section as you need.',
      selector: '[data-tour="automation-add-button"]',
      title: 'Step 2 — add the rule',
    },
  ],
  billing: [
    {
      body: 'Each yearly plan comes with a pool of check-ins sized for your church. Anything beyond is metered at a small per-check-in rate — bigger plans, lower rate.',
      selector: '[data-tour="billing-plan-cards"]',
      title: 'Pick the right size',
    },
  ],
  branding: [
    {
      body: 'Drop in your logo — guests see it at the top of every class.',
      selector: '[data-tour="branding-logo"]',
      title: 'Your logo',
    },
    {
      body: 'Add your icon — it shows up in the browser tab and your PCO sync indicator.',
      selector: '[data-tour="branding-icon"]',
      title: 'Your icon',
    },
    {
      body: 'Pick your button color and text color. Guests see these the moment they land on a class URL.',
      selector: '[data-tour="branding-buttons"]',
      title: 'Buttons',
    },
    {
      body: 'Set the banner color and pick a style — Waves, Lines, Triangles, or None. This is the decorative strip across the top and bottom of every class page.',
      selector: '[data-tour="branding-banner"]',
      title: 'Banner',
    },
    {
      body: 'Live preview — every change you make updates here in real time. This is exactly what guests will see on their phones.',
      selector: '[data-tour="branding-preview"]',
      title: 'Live preview',
    },
  ],
  course: [
    {
      body: 'This is where it starts. Pick a starter template (Starting Point, Membership, Spiritual Gifts) or hit “Start from scratch” for a blank class.',
      selector: '[data-tour="course-new"]',
      title: 'Create your first class',
    },
    {
      body: 'Once you have a few classes, search them by name from right here.',
      selector: '[data-tour="course-search"]',
      title: 'Find a class later',
    },
    {
      body: 'Click any class tile to open its section editor — that’s where you build sections, edit content, and wire automations.',
      selector: '[data-tour="course-existing"]',
      title: 'Edit a class',
    },
    {
      body: 'This opens your public class directory — exactly what guests see when they hit your church URL. Bookmark it for quick spot-checks.',
      selector: '[data-tour="course-public-directory"]',
      title: 'See what guests see',
    },
  ],
  guests: [
    {
      body: 'Search by name or email. Every guest who has ever checked in is here.',
      selector: '[data-tour="guests-search"]',
      title: 'Find someone',
    },
    {
      body: 'Filter by class, status, date range, or sort. Stack filters to answer questions like “who joined Membership last month and hasn’t finished?”',
      selector: '[data-tour="guests-filters"]',
      title: 'Slice the list',
    },
    {
      body: 'Click a guest to open their detail page — every check-in, every form answer, every automation that fired for them.',
      selector: '[data-tour="guests-row"]',
      title: 'Open a guest',
    },
  ],
  ministries: [
    {
      body: 'Add a ministry for each team a guest might be routed to (Worship, Kids, Hospitality, etc.).',
      selector: '[data-tour="ministries-add"]',
      title: 'Add a ministry',
    },
    {
      body: 'Once you have a long list, search by name to find one fast.',
      selector: '[data-tour="ministries-search"]',
      title: 'Search',
    },
    {
      body: 'Click any ministry to edit its name, description, and the assessment results that route guests to it.',
      selector: '[data-tour="ministries-existing"]',
      title: 'Edit a ministry',
    },
  ],
  pco: [
    {
      body: 'Tap to OAuth into Planning Center. Once connected, automations can read and write PCO fields and trigger PCO workflows for every guest who checks in.',
      selector: '[data-tour="pco-connect"]',
      title: 'Connect Planning Center',
    },
  ],
  preview: [
    {
      body: 'Click Preview to open your class in a new tab exactly the way a guest will see it. Even better: scan the page on your phone — that’s where most guests will hit it.',
      selector: '[data-tour="share-preview"]',
      title: 'Open a guest-eye view',
    },
  ],
  profile: [
    {
      body: 'Your URL slug is about to land in every link you share. Lock it in now — once it’s in a newsletter or text blast, changing it breaks every link you sent.',
      selector: '[data-tour="profile-url"]',
      title: 'Your public URL',
    },
    {
      body: 'Pick the timezone your church runs on. Submission timestamps and reports use this.',
      selector: '[data-tour="profile-timezone"]',
      title: 'Timezone',
    },
    {
      body: 'Optional: paste a live-chat embed snippet (Intercom, Help Scout, Drift, etc.) and OnArk drops it on every guest page. Lets guests ask questions mid-class.',
      selector: '[data-tour="profile-live-chat"]',
      title: 'Live chat (optional)',
    },
  ],
  publish: [
    {
      body: 'Click Publish to push the current draft live. A yellow dot here means you have unpublished changes — your guests still see the previous published version until you publish again.',
      selector: '[data-tour="publish-button"]',
      title: 'Publish your class',
    },
  ],
  reports: [
    {
      body: 'Set the time window for every chart and table on this page. Default is all-time, so you see the full picture first.',
      selector: '[data-tour="reports-timerange"]',
      title: 'Pick a range',
    },
    {
      body: 'The trend chart shows total check-ins per day plus completion rate. Hover any point for that day’s numbers.',
      selector: '[data-tour="reports-graph"]',
      title: 'The trend',
    },
    {
      body: 'Per-class breakdown — totals, completion rate, and a Download button for each. This is where you answer “how is X class doing?”',
      selector: '[data-tour="reports-breakdown"]',
      title: 'By class',
    },
    {
      body: 'Every card has a Download button. Exports the rows as CSV you can paste into a staff email or pivot in Sheets.',
      selector: '[data-tour="reports-download"]',
      title: 'Export to CSV',
    },
  ],
  sections: [
    {
      body: 'Every class is built from sections, listed here on the left. Click any one to edit it.',
      selector: '[data-tour="section-sidebar"]',
      title: 'The section sidebar',
    },
    {
      body: 'Add new sections with this button. Seven types: Check-in, Form, Text, Video, Test, Branch, Completion. Each gets its own mini-tour when you first open it.',
      selector: '[data-tour="section-create"]',
      title: 'Add a section',
    },
    {
      body: 'Drag the handles to reorder. The first Check-in section is always how guests log in — keep it first.',
      selector: '[data-tour="section-reorder"]',
      title: 'Reorder',
    },
    {
      body: 'Class Settings holds class-wide stuff: name, description, language, whether guests can go back, banner image. Tucked away so it doesn’t crowd the list.',
      selector: '[data-tour="section-class-settings"]',
      title: 'Class Settings',
    },
    {
      body: 'Automations live at the class level — clicking here opens the full builder where you wire emails, PCO fields, and PCO workflows to each section’s responses.',
      selector: '[data-tour="section-automations"]',
      title: 'Automations',
    },
  ],
  share: [
    {
      body: 'Tap here to copy your class link. Drop it in a newsletter, sign-up form, text blast, or QR code.',
      selector: '[data-tour="share-copy-link"]',
      title: 'Copy the link',
    },
    {
      body: 'Still nervous? Click Preview to walk the class once yourself before sending — best way to spot confusing copy or broken automations.',
      selector: '[data-tour="share-preview"]',
      title: 'One last preview',
    },
  ],
  team: [
    {
      body: 'Hit “Invite” and enter their email. New teammates join as Editors, who can build and edit classes.',
      selector: '[data-tour="team-invite"]',
      title: 'Invite a teammate',
    },
    {
      body: 'Each member’s role lives here. Owner = full access including billing. Editor = build and edit classes.',
      selector: '[data-tour="team-role"]',
      title: 'Roles, plainly',
    },
    {
      body: '“Make owner” transfers the keys (you keep your seat). “Remove” takes them out of this church — their account stays alive, they just lose access here.',
      selector: '[data-tour="team-actions"]',
      title: 'Promote or remove',
    },
  ],
};

// Tours that fire when a specific modal/area opens for the first time in
// the session — see ModalTour.tsx. Keyed by a stable string used as the
// session-seen key.
export const modalTours: Record<string, TourStep[]> = {
  classSettings: [
    {
      body: 'The class name shows up in your admin list, on every guest page, and in reports. Make it the thing your church actually calls this class.',
      selector: '[data-tour="class-settings-name"]',
      title: 'Class name',
    },
    {
      body: 'This is the public URL guests will hit. The slug is part of every link you share — once a link goes out, changing the slug breaks anything already in the wild.',
      selector: '[data-tour="class-settings-url"]',
      title: 'Your class URL',
    },
    {
      body: 'Rich-text intro that guests see on the public class directory and at the top of the class page. Sell what the class is and why it matters.',
      selector: '[data-tour="class-settings-description"]',
      title: 'Description',
    },
    {
      body: 'Public classes show up on your church directory (the page guests see at your church URL). Uncheck if you want a class that only opens for people you’ve sent the direct link to.',
      selector: '[data-tour="class-settings-public"]',
      title: 'Public on the directory',
    },
    {
      body: 'Duplicate makes a draft copy you can edit without touching the live one — handy for seasonal variants. Delete is permanent; guests with the old link see a not-found page.',
      selector: '[data-tour="class-settings-actions"]',
      title: 'Duplicate or delete',
    },
  ],
};
